/**
 * Costing & BOM section (garment Increment 2, M3) — section 4 of the Style File.
 *
 * Faithful to `prototype/garment/style-file.html` #sec-costing: a two-column
 * cost summary (build-up lines → Quoted FOB · wastage / realised-margin /
 * RoSCTL / BOM-count kv-rows) once locked, and an inline cost-sheet editor
 * while the order is `po_confirmed`. The breakdown shown is always the
 * server-computed figure (the calc is the single source of truth — the web
 * bundle never re-implements the paise math).
 *
 * @see prototype/garment/style-file.html #sec-costing
 * @see packages/domain/src/garment/costing
 */
import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Calculator, Check, Info, Lock, Plus, Save, Trash2, X } from "lucide-react";
import { Button, Input, toast } from "@flint/ui";
import type { costing as costingContract } from "@flint/api-contract";
import { trpc } from "../../../lib/trpc.js";
import { formatDate, bpToPercent, paiseToMoney } from "../../../lib/format.js";
import type { StyleStatus } from "../lib/status.js";
import { CostLineItemPicker } from "./cost-line-item-picker.js";
import { FormSelect } from "./form-select.js";

type CostingData = costingContract.CostingDetailWire;
type Breakdown = CostingData["breakdown"];

interface CostingSectionProps {
  styleId: string;
  styleStatus: StyleStatus;
  currency: string;
  orderQty: number;
  styleName: string;
  styleRef: string;
  buyerName: string;
}

type CostLineKind = "fabric" | "trim" | "packing" | "process";

interface EditorLine {
  kind: CostLineKind;
  label: string;
  /** Linked catalog item — null means this line stays free text (no stock/rate match downstream). */
  itemId: string | null;
  consumption: string; // human decimal, e.g. "2.4"
  uom: string;
  rate: string; // human rupees, e.g. "240"
  wastage: string; // human percent, e.g. "7"
}

const KIND_LABEL: Record<CostLineKind, string> = {
  fabric: "Fabric",
  trim: "Trim",
  packing: "Packing",
  process: "Process / job-work",
};
const KIND_OPTIONS = (["fabric", "trim", "packing", "process"] as const).map((k) => ({ value: k, label: KIND_LABEL[k] }));

export function CostingSection({ styleId, styleStatus, currency, orderQty, styleName, styleRef, buyerName }: CostingSectionProps) {
  const utils = trpc.useUtils();
  const query = trpc.costing.get.useQuery({ styleId });

  const invalidate = async () => {
    await Promise.all([
      utils.costing.get.invalidate({ styleId }),
      utils.styles.get.invalidate({ id: styleId }),
      utils.styles.list.invalidate(),
      // Locking generates the BOM → refresh the Materials section.
      utils.materials.getForStyle.invalidate({ styleId }),
      utils.auditLog.list.invalidate(),
    ]);
  };

  const save = trpc.costing.save.useMutation({
    onSuccess: async () => {
      await invalidate();
      toast.success("Costing saved", { description: "Review the cost summary, then lock to quote." });
    },
    onError: (e: { message: string }) => toast.error("Couldn't save costing", { description: e.message }),
  });
  const lock = trpc.costing.lock.useMutation({
    onSuccess: async (res: { bomLineCount: number }) => {
      await invalidate();
      toast.success("Costing locked", { description: `Quoted FOB set · ${res.bomLineCount} BOM lines generated.` });
    },
    onError: (e: { message: string }) => toast.error("Couldn't lock costing", { description: e.message }),
  });

  if (styleStatus === "draft") {
    return (
      <div className="rounded-sm border border-dashed border-line bg-bg px-4 py-6 text-center">
        <p className="text-sm font-medium text-ink-2">Confirm the order to start costing</p>
        <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-muted">
          The cost sheet needs the confirmed order quantity to scale the bill of materials.
        </p>
      </div>
    );
  }

  if (query.isLoading) return <p className="text-sm text-muted">Loading costing…</p>;

  const costing = query.data;
  const locked = costing?.status === "locked";

  // ── Locked: the prototype's read-only cost summary + BOM ──
  if (locked && costing) {
    return (
      <LockedCosting
        styleId={styleId}
        costing={costing}
        currency={currency}
        orderQty={orderQty}
        styleName={styleName}
        styleRef={styleRef}
        buyerName={buyerName}
      />
    );
  }

  // ── po_confirmed, draft (or no costing yet): the editor ──
  return (
    <CostingEditor
      key={costing?.id ?? "new"}
      styleId={styleId}
      costing={costing ?? null}
      currency={currency}
      orderQty={orderQty}
      styleName={styleName}
      styleRef={styleRef}
      buyerName={buyerName}
      saving={save.isPending}
      locking={lock.isPending}
      onSave={(payload) => save.mutate({ styleId, ...payload })}
      onLock={() => lock.mutate({ styleId })}
    />
  );
}

/* ── Editor (po_confirmed → draft costing) ──────────────────────── */

interface SavePayload {
  samMinutesMilli: number;
  cmRatePaisePerMin: string;
  efficiencyBp: number;
  overheadBp: number;
  marginBp: number;
  lines: Array<{
    kind: CostLineKind;
    label: string;
    itemId: string | null;
    consumptionMilli: number;
    uom: string;
    ratePaisePerUnit: string;
    wastageBp: number;
  }>;
}

function CostingEditor({
  styleId,
  costing,
  currency,
  orderQty,
  styleName,
  styleRef,
  buyerName,
  saving,
  locking,
  onSave,
  onLock,
}: {
  styleId: string;
  costing: CostingData | null;
  currency: string;
  orderQty: number;
  styleName: string;
  styleRef: string;
  buyerName: string;
  saving: boolean;
  locking: boolean;
  onSave: (payload: SavePayload) => void;
  onLock: () => void;
}) {
  const [sam, setSam] = useState(costing ? milliToDecimal(costing.samMinutesMilli) : "");
  const [cmRate, setCmRate] = useState(costing ? paiseToRupees(costing.cmRatePaisePerMin) : "");
  const [efficiency, setEfficiency] = useState(costing ? bpToPercent(costing.efficiencyBp) : "55");
  const [overhead, setOverhead] = useState(costing ? bpToPercent(costing.overheadBp) : "8");
  const [margin, setMargin] = useState(costing ? bpToPercent(costing.marginBp) : "");
  const [lines, setLines] = useState<EditorLine[]>(
    costing && costing.breakdown.lines.length > 0
      ? costing.breakdown.lines.map((l) => ({
          kind: l.kind,
          label: l.label,
          itemId: l.itemId,
          consumption: milliToDecimal(l.consumptionMilli),
          uom: l.uom,
          rate: paiseToRupees(l.ratePaisePerUnit),
          wastage: bpToPercent(l.wastageBp),
        }))
      : [{ kind: "fabric", label: "", itemId: null, consumption: "", uom: "m", rate: "", wastage: "" }],
  );

  const updateLine = (i: number, patch: Partial<EditorLine>) =>
    setLines((cur) => cur.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLine = () =>
    setLines((cur) => [...cur, { kind: "trim", label: "", itemId: null, consumption: "1", uom: "pcs", rate: "", wastage: "" }]);
  const removeLine = (i: number) => setLines((cur) => cur.filter((_, idx) => idx !== i));

  const buildPayload = (): SavePayload | null => {
    const samMilli = decimalToMilli(sam);
    const cmPaise = rupeesToPaiseStr(cmRate);
    const effBp = percentToBp(efficiency);
    const ohBp = percentToBp(overhead);
    const marginBp = percentToBp(margin);
    if (samMilli === null || cmPaise === null || effBp === null || ohBp === null || marginBp === null) return null;
    const payloadLines = [];
    for (const l of lines) {
      if (l.label.trim() === "") continue; // skip blank rows
      const consMilli = decimalToMilli(l.consumption);
      const ratePaise = rupeesToPaiseStr(l.rate);
      const wastageBp = percentToBp(l.wastage === "" ? "0" : l.wastage);
      if (consMilli === null || ratePaise === null || wastageBp === null) return null;
      payloadLines.push({
        kind: l.kind,
        label: l.label.trim(),
        itemId: l.itemId,
        consumptionMilli: consMilli,
        uom: l.uom.trim() || "pcs",
        ratePaisePerUnit: ratePaise,
        wastageBp,
      });
    }
    return { samMinutesMilli: samMilli, cmRatePaisePerMin: cmPaise, efficiencyBp: effBp, overheadBp: ohBp, marginBp, lines: payloadLines };
  };

  const handleSave = () => {
    const payload = buildPayload();
    if (!payload) {
      toast.error("Check the cost sheet", { description: "Enter valid numbers for SAM, rates, efficiency and margin." });
      return;
    }
    if (payload.lines.length === 0) {
      toast.error("Add a material line", { description: "A cost sheet needs at least one fabric or trim line." });
      return;
    }
    onSave(payload);
  };

  const canLock = costing !== null && costing.status === "draft" && BigInt(costing.breakdown.fobPaise) > 0n;
  const readyPayload = buildPayload();
  const ready = readyPayload !== null && readyPayload.lines.length > 0;

  // ── Live preview — debounce the same payload Save would send, so the
  // right-hand panel recalculates as the sheet is typed with no click and
  // no implicit autosave (`costing.preview` persists nothing). ──
  const [debouncedPayload, setDebouncedPayload] = useState<SavePayload | null>(null);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedPayload(buildPayload()), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sam, cmRate, efficiency, overhead, margin, lines]);

  const preview = trpc.costing.preview.useQuery(
    debouncedPayload ? { styleId, ...debouncedPayload } : { styleId, samMinutesMilli: 0, cmRatePaisePerMin: "0", efficiencyBp: 1, overheadBp: 0, marginBp: 0, lines: [] },
    { enabled: debouncedPayload !== null },
  );

  const [lastBreakdown, setLastBreakdown] = useState<Breakdown | null>(costing?.breakdown ?? null);
  useEffect(() => {
    if (preview.data) setLastBreakdown(preview.data);
  }, [preview.data]);

  const materialTotalPaise = lastBreakdown
    ? (BigInt(lastBreakdown.fabricPaise) + BigInt(lastBreakdown.trimsPaise) + BigInt(lastBreakdown.packingPaise)).toString()
    : null;
  const orderTotalPaise = lastBreakdown ? (BigInt(lastBreakdown.fobPaise) * BigInt(orderQty)).toString() : null;

  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
      <div className="flex min-w-0 flex-col gap-5">
        <FieldSectionCard step="1" title="Cut & make" description="The per-piece labour build-up.">
          <div className="flex flex-col gap-5 lg:flex-row lg:divide-x lg:divide-line">
            <div className="lg:pr-6">
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.08em] text-muted">Labour build-up</p>
              <div className="grid grid-cols-2 gap-5 sm:grid-cols-3">
                <NumField
                  label="SAM (min)"
                  hint="Standard Allotted Minutes — how long one operator takes to make one piece. Drives the labour cost below."
                  value={sam}
                  onChange={setSam}
                  placeholder="13.2"
                />
                <NumField
                  label={`CM rate (${currency}/min)`}
                  hint="Cut & Make rate — what you pay the factory per minute of stitching labour."
                  value={cmRate}
                  onChange={setCmRate}
                  placeholder="6.50"
                />
                <NumField
                  label="Efficiency %"
                  hint="Line efficiency — how close the floor runs to the SAM. Lower efficiency spreads the same labour cost over fewer good pieces, raising cost/pc."
                  value={efficiency}
                  onChange={setEfficiency}
                  placeholder="55"
                />
              </div>
              <div className="mt-2.5">
                <FormulaRow
                  formula={`${sam || "—"} min × ${currency} ${cmRate || "—"} ÷ ${efficiency || "—"}% eff`}
                  result={lastBreakdown ? paiseToMoney(lastBreakdown.cmPaise, currency) : null}
                  label="= CM cost/pc"
                />
              </div>
            </div>
            <div className="lg:max-w-[280px] lg:pl-6">
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.08em] text-muted">Pricing add-ons</p>
              <div className="grid grid-cols-2 gap-5">
                <NumField
                  label="Overhead %"
                  hint="Factory overhead — rent, power, admin — added on top of material + labour cost."
                  value={overhead}
                  onChange={setOverhead}
                  placeholder="8"
                />
                <NumField
                  label="Margin %"
                  hint="Profit margin added on top of factory cost to arrive at the quoted FOB price."
                  value={margin}
                  onChange={setMargin}
                  placeholder="14.6"
                />
              </div>
              <div className="mt-2.5 flex flex-col gap-1">
                <FormulaRow
                  formula={`${overhead || "—"}% × subtotal`}
                  result={lastBreakdown ? paiseToMoney(lastBreakdown.overheadPaise, currency) : null}
                  label="= Overhead/pc"
                />
                <FormulaRow
                  formula={`${margin || "—"}% of FOB`}
                  result={lastBreakdown ? paiseToMoney(lastBreakdown.marginPaise, currency) : null}
                  label="= Margin/pc"
                />
              </div>
            </div>
          </div>
        </FieldSectionCard>

        <FieldSectionCard
          step="2"
          title="Materials & processes"
          description="Fabric, trims, packing and job-work — becomes the BOM when locked."
          action={
            <button type="button" onClick={addLine} className="inline-flex items-center gap-1 rounded-sm border border-line bg-paper px-2 py-1 text-xs font-medium text-ink-2 transition hover:bg-bg">
              <Plus size={13} /> Add line
            </button>
          }
          bodyless
        >
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] table-fixed border-collapse text-sm">
              <thead>
                <tr className="border-b border-line bg-raised/60 text-xs uppercase tracking-wide text-muted">
                  <th className="w-[112px] py-2.5 pl-[18px] pr-3 text-left font-medium">Kind</th>
                  <th className="px-3 py-2.5 text-left font-medium">Description</th>
                  <th className="w-[86px] whitespace-nowrap px-3 py-2.5 text-center font-medium">Stock</th>
                  <th className="w-[76px] whitespace-nowrap px-3 py-2.5 text-right font-medium">Per pc</th>
                  <th className="w-[64px] whitespace-nowrap px-3 py-2.5 text-left font-medium">UOM</th>
                  <th className="w-[88px] whitespace-nowrap px-3 py-2.5 text-right font-medium">Rate ({currency})</th>
                  <th className="w-[68px] whitespace-nowrap px-3 py-2.5 text-right font-medium">Wst %</th>
                  <th className="w-9 py-2.5 pr-[18px]" />
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i} className="border-b border-line last:border-0">
                    <td className="py-2.5 pl-[18px] pr-3">
                      <FormSelect
                        ariaLabel={`Kind line ${i + 1}`}
                        value={l.kind}
                        onValueChange={(v) => updateLine(i, { kind: v as CostLineKind })}
                        options={KIND_OPTIONS}
                        triggerClassName="h-8 w-full px-2 text-sm"
                        itemClassName="text-sm"
                      />
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <Input aria-label={`Description line ${i + 1}`} value={l.label} onChange={(e) => { const label = e.currentTarget.value; updateLine(i, { label }); }} placeholder="Cotton Jersey 180 GSM" className="h-8 w-full text-sm" />
                        <CostLineItemPicker
                          onPick={({ itemId, label, uom, rate }) => updateLine(i, { itemId, label, uom, ...(rate ? { rate } : {}) })}
                        />
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      {l.itemId ? (
                        <span
                          className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-emerald-700"
                          title="Linked to a catalog item — stock and rate history will show in Sourcing"
                        >
                          <Check size={10} /> Linked
                          <button
                            type="button"
                            aria-label={`Unlink catalog item from line ${i + 1}`}
                            onClick={() => updateLine(i, { itemId: null })}
                            className="ml-0.5 rounded-full hover:text-emerald-900"
                          >
                            <X size={10} />
                          </button>
                        </span>
                      ) : (
                        <span className="text-xs text-muted" title="Free text — no stock or rate history match">
                          Not linked
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <input aria-label={`Consumption line ${i + 1}`} value={l.consumption} onChange={(e) => { const consumption = e.currentTarget.value; updateLine(i, { consumption }); }} placeholder="2.4" inputMode="decimal" className="h-8 w-full rounded-sm border border-line bg-paper px-2 text-right font-mono text-sm text-ink outline-none focus-visible:border-sky-600" />
                    </td>
                    <td className="px-3 py-2.5">
                      <input aria-label={`UOM line ${i + 1}`} value={l.uom} onChange={(e) => { const uom = e.currentTarget.value; updateLine(i, { uom }); }} placeholder="m" className="h-8 w-full rounded-sm border border-line bg-paper px-2 text-sm text-ink outline-none focus-visible:border-sky-600" />
                    </td>
                    <td className="px-3 py-2.5">
                      <input aria-label={`Rate line ${i + 1}`} value={l.rate} onChange={(e) => { const rate = e.currentTarget.value; updateLine(i, { rate }); }} placeholder="240" inputMode="decimal" className="h-8 w-full rounded-sm border border-line bg-paper px-2 text-right font-mono text-sm text-ink outline-none focus-visible:border-sky-600" />
                    </td>
                    <td className="px-3 py-2.5">
                      <input aria-label={`Wastage line ${i + 1}`} value={l.wastage} onChange={(e) => { const wastage = e.currentTarget.value; updateLine(i, { wastage }); }} placeholder="0" inputMode="decimal" className="h-8 w-full rounded-sm border border-line bg-paper px-2 text-right font-mono text-sm text-ink outline-none focus-visible:border-sky-600" />
                    </td>
                    <td className="py-2.5 pr-[18px] text-right">
                      <button type="button" aria-label={`Remove line ${i + 1}`} onClick={() => removeLine(i)} disabled={lines.length === 1} className="grid h-7 w-7 place-items-center rounded-sm text-muted transition hover:bg-bad-50 hover:text-bad disabled:opacity-40">
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="px-[18px] py-2.5 text-xs text-muted">BOM scales to the order qty ({orderQty.toLocaleString("en-IN")} pcs) when you lock.</p>
        </FieldSectionCard>

        {/* What locking does — sits directly above the action bar it explains. */}
        <div className="grid grid-cols-1 gap-4 rounded-md border border-line bg-raised/40 px-4 py-3 sm:grid-cols-3 sm:divide-x sm:divide-line">
          <LockStep icon={Lock} label="On lock" value="Quoted FOB is set and frozen" />
          <LockStep icon={ArrowRight} label="Then" value="BOM is generated from these lines" />
          <LockStep icon={Check} label="Next step" value="Sourcing unlocks against the new BOM" />
        </div>

        {/* Footer bar — readiness chip + summary + actions, matching the PO composer's `.composer-foot`. */}
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-line bg-paper px-4 py-3 shadow-sm">
          <span
            className={`inline-flex h-7 shrink-0 items-center gap-1 rounded-full px-3 text-xs font-semibold ${
              ready ? "bg-emerald-50 text-emerald-700" : "bg-warn-50 text-warn"
            }`}
          >
            <Check size={13} aria-hidden />
            {ready ? "Ready to save" : "A priced material line needed"}
          </span>
          <span className="min-w-0 flex-1 truncate text-sm text-muted">
            {lastBreakdown ? `Quoted FOB ${paiseToMoney(lastBreakdown.fobPaise, currency)}/pc` : "No cost calculated yet"}
          </span>
          <Button disabled={saving} onClick={handleSave}><Save size={15} /> {costing ? "Update cost sheet" : "Save cost sheet"}</Button>
          <Button intent="ghost" className="border border-line bg-paper" disabled={!canLock || locking} onClick={onLock} title={canLock ? "Lock the costing, set the quoted FOB, and generate the BOM" : "Save the cost sheet first"}>
            <Lock size={15} /> Lock costing
          </Button>
        </div>
      </div>

      {/* Live document-style preview — recalculates as the sheet is typed, no Save click needed. */}
      <aside className="xl:sticky xl:top-4 xl:self-start">
        <div className="overflow-hidden rounded-md border border-line bg-paper shadow-sm">
          <header className="flex items-center justify-between gap-3 border-b border-line bg-raised px-[18px] py-3.5">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-ink">
              <span className="h-[7px] w-[7px] shrink-0 animate-pulse rounded-full bg-emerald-500 shadow-[0_0_0_3px] shadow-emerald-50" />
              Cost sheet preview
            </h3>
            {preview.isFetching ? <span className="text-xs font-medium uppercase tracking-wide text-muted">Calculating…</span> : null}
          </header>

          <div className="px-[22px] pb-6 pt-[22px] text-xs leading-[1.55] text-ink-2">
            <div className="mb-3.5 flex items-start justify-between gap-[18px] border-b border-line pb-3.5">
              <div className="min-w-0">
                <span className="mb-0.5 block text-sm font-bold tracking-tight text-ink">{styleName}</span>
                <span className="block text-xs leading-normal text-muted">{buyerName}</span>
              </div>
              <div className="shrink-0 text-right text-xs font-semibold uppercase tracking-[0.08em] text-muted">
                Cost sheet
                <span className="mt-1 block font-mono text-sm font-bold normal-case tracking-tight text-ink">{styleRef}</span>
                <span className="mt-1.5 inline-flex items-center gap-1.5 rounded-full border border-line bg-raised px-2 py-0.5 text-xs font-semibold uppercase tracking-[0.06em] text-muted">
                  {costing ? "Draft" : "Unsaved"}
                </span>
              </div>
            </div>

            <div className="mb-3.5 grid grid-cols-2 gap-x-4 gap-y-2.5 border-b border-line pb-3.5">
              <PreviewMetaCell label="SAM · CM rate">
                {sam || "—"} min × {currency} {cmRate || "—"}/min
              </PreviewMetaCell>
              <PreviewMetaCell label="Efficiency">{efficiency || "—"}%</PreviewMetaCell>
              <PreviewMetaCell label="Overhead · Margin">
                {overhead || "—"}% · {margin || "—"}%
              </PreviewMetaCell>
              <PreviewMetaCell label="Order qty">{orderQty.toLocaleString("en-IN")} pcs</PreviewMetaCell>
            </div>

            {lastBreakdown ? (
              <>
                <table className="mb-3 w-full border-collapse text-xs">
                  <thead>
                    <tr>
                      <th className="border-b border-line py-1.5 pr-2 text-left text-xs font-semibold uppercase tracking-[0.06em] text-muted">Item</th>
                      <th className="whitespace-nowrap border-b border-line py-1.5 pl-2 text-right text-xs font-semibold uppercase tracking-[0.06em] text-muted">Per pc</th>
                      <th className="border-b border-line py-1.5 pl-2 text-right text-xs font-semibold uppercase tracking-[0.06em] text-muted">Rate</th>
                      <th className="border-b border-line py-1.5 pl-2 text-right text-xs font-semibold uppercase tracking-[0.06em] text-muted">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lastBreakdown.lines.map((l, i) => (
                      <tr key={i} className={i < lastBreakdown.lines.length - 1 ? "border-b border-line" : ""}>
                        <td className="py-1.5 pr-2 align-top text-ink">
                          {l.label}
                          <span className="block text-xs capitalize text-muted">{l.kind}</span>
                        </td>
                        <td className="py-1.5 pl-2 text-right align-top font-mono tabular-nums whitespace-nowrap">{milliToDecimal(l.consumptionMilli)} {l.uom}</td>
                        <td className="py-1.5 pl-2 text-right align-top font-mono tabular-nums whitespace-nowrap">{paiseToMoney(l.ratePaisePerUnit, currency)}</td>
                        <td className="py-1.5 pl-2 text-right align-top font-mono tabular-nums whitespace-nowrap">{paiseToMoney(l.perPiecePaise, currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="ml-auto mt-1 max-w-[260px] border-t border-line pt-3">
                  <PreviewTotalRow label="Material total" value={paiseToMoney(materialTotalPaise!, currency)} />
                  <PreviewTotalRow label={`CM (SAM ${milliToDecimal(decimalToMilli(sam) ?? 0)} ÷ ${efficiency || "—"}% eff)`} value={paiseToMoney(lastBreakdown.cmPaise, currency)} />
                  {BigInt(lastBreakdown.processPaise) > 0n ? <PreviewTotalRow label="Process / job-work" value={paiseToMoney(lastBreakdown.processPaise, currency)} /> : null}
                  <PreviewTotalRow label={`Overhead (${overhead || "—"}%)`} value={paiseToMoney(lastBreakdown.overheadPaise, currency)} />
                  <PreviewTotalRow label={`Margin (${margin || "—"}%)`} value={paiseToMoney(lastBreakdown.marginPaise, currency)} />
                  <div className="mt-1.5 flex items-center justify-between gap-3 border-t border-line pb-1 pt-2 text-sm font-bold text-ink">
                    <span>Quoted FOB / pc</span>
                    <span className="font-mono text-sm font-bold tracking-tight tabular-nums">{paiseToMoney(lastBreakdown.fobPaise, currency)}</span>
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-3 rounded-sm bg-bg px-2.5 py-2 text-sm font-bold text-ink">
                    <span>Order total ({orderQty.toLocaleString("en-IN")} pcs)</span>
                    <span className="font-mono text-sm font-bold tracking-tight tabular-nums">{paiseToMoney(orderTotalPaise!, currency)}</span>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center gap-2 rounded-md border border-dashed border-line bg-raised/40 px-6 py-9 text-center">
                <span className="grid h-9 w-9 place-items-center rounded-full bg-raised text-muted">
                  <Calculator size={16} aria-hidden />
                </span>
                <p className="text-sm font-semibold text-ink">Nothing costed yet</p>
                <p className="max-w-[230px] text-xs leading-relaxed text-muted">
                  Fill in Cut &amp; make and add a priced material line — the FOB build-up appears here as you type.
                </p>
              </div>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
}

function LockedCosting({
  styleId,
  costing,
  currency,
  orderQty,
  styleName,
  styleRef,
  buyerName,
}: {
  styleId: string;
  costing: CostingData;
  currency: string;
  orderQty: number;
  styleName: string;
  styleRef: string;
  buyerName: string;
}) {
  const b = costing.breakdown;
  const fabricWastage = b.lines.find((l) => l.kind === "fabric")?.wastageBp ?? 0;
  const trimWastage = b.lines.find((l) => l.kind === "trim")?.wastageBp ?? 0;
  const materialTotalPaise = (BigInt(b.fabricPaise) + BigInt(b.trimsPaise) + BigInt(b.packingPaise)).toString();
  const orderTotalPaise = (BigInt(b.fobPaise) * BigInt(orderQty)).toString();

  // Read-only stock coverage — same `materials.getForStyle` data the Sourcing
  // screen uses, joined onto the BOM by line id. No actions here; this is
  // the frozen cost sheet, not the working Sourcing table.
  const availability = trpc.materials.getForStyle.useQuery({ styleId });
  const availLines = availability.data?.lines ?? [];
  const availabilityByBomLine = new Map(availLines.map((r) => [r.bomLineId, r]));
  // Classified on `plan`, the field the payload actually carries — these
  // three read `r.status`, which has never existed on `materialsView`, so
  // every stat rendered 0. Mapping matches `sourcing-summary-card.tsx`:
  // to_source + partly_ready = still to buy, awaiting = on order.
  const sufficientCount = availLines.filter(
    (r) => r.plan === "ready" || r.plan === "issued",
  ).length;
  const awaitingCount = availLines.filter((r) => r.plan === "awaiting").length;
  const needsOrderCount = availLines.filter(
    (r) => r.plan === "to_source" || r.plan === "partly_ready",
  ).length;

  return (
    <div className="flex flex-col gap-5">
      <div className="grid gap-5 lg:grid-cols-3">
      {/* Frozen cost sheet — same document chrome as the live preview, minus the live-dot. */}
      <div className="overflow-hidden rounded-md border border-line bg-paper shadow-sm lg:col-span-1">
        <header className="flex items-center justify-between gap-3 border-b border-line bg-raised px-[22px] py-4">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-ink">
            <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-ink/[0.06] text-ink-2">
              <Lock size={12} aria-hidden />
            </span>
            Cost sheet
          </h3>
          {costing.lockedAt ? <span className="text-xs font-medium text-muted">Locked {formatDate(costing.lockedAt)}</span> : null}
        </header>

        <div className="px-[26px] pb-7 pt-6 text-xs leading-[1.55] text-ink-2">
          <div className="mb-4 flex items-start justify-between gap-[18px] border-b border-line pb-4">
            <div className="min-w-0">
              <span className="mb-0.5 block text-base font-bold tracking-tight text-ink">{styleName}</span>
              <span className="block text-xs leading-normal text-muted">{buyerName}</span>
            </div>
            <div className="shrink-0 text-right text-xs font-semibold uppercase tracking-[0.08em] text-muted">
              Cost sheet
              <span className="mt-1 block font-mono text-base font-bold normal-case tracking-tight text-ink">{styleRef}</span>
              <span className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold uppercase tracking-[0.06em] text-emerald-700">
                <Lock size={10} aria-hidden /> Locked
              </span>
            </div>
          </div>

          <div className="mb-4 grid grid-cols-2 gap-x-6 gap-y-3 border-b border-line pb-4">
            <PreviewMetaCell label="SAM · CM rate">
              {milliToDecimal(costing.samMinutesMilli)} min × {paiseToMoney(costing.cmRatePaisePerMin, currency)}/min
            </PreviewMetaCell>
            <PreviewMetaCell label="Efficiency">{bpToPercent(costing.efficiencyBp)}%</PreviewMetaCell>
            <PreviewMetaCell label="Overhead · Margin">
              {bpToPercent(costing.overheadBp)}% · {bpToPercent(costing.marginBp)}%
            </PreviewMetaCell>
            <PreviewMetaCell label="Order qty">{orderQty.toLocaleString("en-IN")} pcs</PreviewMetaCell>
          </div>

          <table className="mb-4 w-full border-collapse text-xs">
            <thead>
              <tr>
                <th className="border-b border-line py-2 pr-2 text-left text-xs font-semibold uppercase tracking-[0.06em] text-muted">Item</th>
                <th className="whitespace-nowrap border-b border-line py-2 pl-2 text-right text-xs font-semibold uppercase tracking-[0.06em] text-muted">Per pc</th>
                <th className="border-b border-line py-2 pl-2 text-right text-xs font-semibold uppercase tracking-[0.06em] text-muted">Rate</th>
                <th className="border-b border-line py-2 pl-2 text-right text-xs font-semibold uppercase tracking-[0.06em] text-muted">Amount</th>
              </tr>
            </thead>
            <tbody>
              {b.lines.map((l, i) => (
                <tr key={i} className={i < b.lines.length - 1 ? "border-b border-line" : ""}>
                  <td className="py-2 pr-2 align-top text-ink">
                    {l.label}
                    <span className="block text-xs capitalize text-muted">{l.kind}</span>
                  </td>
                  <td className="whitespace-nowrap py-2 pl-2 text-right align-top font-mono tabular-nums">{milliToDecimal(l.consumptionMilli)} {l.uom}</td>
                  <td className="whitespace-nowrap py-2 pl-2 text-right align-top font-mono tabular-nums">{paiseToMoney(l.ratePaisePerUnit, currency)}</td>
                  <td className="whitespace-nowrap py-2 pl-2 text-right align-top font-mono tabular-nums">{paiseToMoney(l.perPiecePaise, currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="ml-auto max-w-[300px] border-t border-line pt-3.5">
            <PreviewTotalRow label="Material total" value={paiseToMoney(materialTotalPaise, currency)} />
            <PreviewTotalRow
              label={`CM (SAM ${milliToDecimal(costing.samMinutesMilli)} ÷ ${bpToPercent(costing.efficiencyBp)}% eff)`}
              value={paiseToMoney(b.cmPaise, currency)}
            />
            {BigInt(b.processPaise) > 0n ? <PreviewTotalRow label="Process / job-work" value={paiseToMoney(b.processPaise, currency)} /> : null}
            <PreviewTotalRow label={`Overhead (${bpToPercent(costing.overheadBp)}%)`} value={paiseToMoney(b.overheadPaise, currency)} />
            <PreviewTotalRow label={`Margin (${bpToPercent(costing.marginBp)}%)`} value={paiseToMoney(b.marginPaise, currency)} />
            <div className="mt-2 flex items-center justify-between gap-3 border-t border-line pb-1 pt-2.5 text-sm font-bold text-ink">
              <span>Quoted FOB / pc</span>
              <span className="font-mono text-base font-bold tracking-tight tabular-nums">{paiseToMoney(b.fobPaise, currency)}</span>
            </div>
            <div className="mt-2.5 flex items-center justify-between gap-3 rounded-sm bg-bg px-3 py-2.5 text-sm font-bold text-ink">
              <span>Order total ({orderQty.toLocaleString("en-IN")} pcs)</span>
              <span className="font-mono text-base font-bold tracking-tight tabular-nums">{paiseToMoney(orderTotalPaise, currency)}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-5 gap-y-3 border-t border-line px-[22px] py-4">
          <PreviewMetaCell label="Wastage allowed">
            Fabric {bpToPercent(fabricWastage)}% · trims {bpToPercent(trimWastage)}%
          </PreviewMetaCell>
          <PreviewMetaCell label="Realised margin">Lights up at dispatch</PreviewMetaCell>
          <PreviewMetaCell label="RoSCTL eligible">Lands at dispatch (Inc 8)</PreviewMetaCell>
          <PreviewMetaCell label="BOM lines">{costing.bomLines.length} · auto-generated</PreviewMetaCell>
        </div>
      </div>

      {costing.bomLines.length > 0 ? (
        <div className="lg:col-span-2">
          <FieldSectionCard
            title="Bill of materials"
            description="Material requirement, scaled to the order qty at lock — stock coverage from the warehouse alongside it."
            action={
              <Link
                to="/merchandising/styles/$styleId/materials"
                params={{ styleId }}
                className="inline-flex items-center gap-1 text-xs font-semibold text-sky-700 hover:text-sky-800"
              >
                Go to Sourcing <ArrowRight size={12} />
              </Link>
            }
            bodyless
          >
            <div className="grid grid-cols-3 divide-x divide-line border-b border-line">
              <Stat label="Covered from stock" value={sufficientCount} />
              <Stat label="Awaiting inward" value={awaitingCount} />
              <Stat label="Needs ordering" value={needsOrderCount} tone={needsOrderCount > 0 ? "warn" : undefined} />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[680px] border-collapse text-sm">
                <thead>
                  <tr className="border-b border-line bg-raised/60 text-xs uppercase tracking-wide text-muted">
                    <th className="py-2.5 pl-[18px] pr-3 text-left font-medium">Material</th>
                    <th className="whitespace-nowrap px-3 py-2.5 text-left font-medium">Kind</th>
                    <th className="whitespace-nowrap px-3 py-2.5 text-right font-medium">Per pc</th>
                    <th className="whitespace-nowrap px-3 py-2.5 text-right font-medium">Wastage</th>
                    <th className="whitespace-nowrap px-3 py-2.5 text-right font-medium">Required</th>
                    <th className="whitespace-nowrap px-3 py-2.5 text-right font-medium">From stock</th>
                    <th className="whitespace-nowrap py-2.5 pl-3 pr-[18px] text-right font-medium">To order</th>
                  </tr>
                </thead>
                <tbody>
                  {costing.bomLines.map((r) => {
                    const avail = availabilityByBomLine.get(r.id);
                    const requiredMilli = Number(r.requiredQtyMilli);
                    const fromStockMilli = avail ? Math.min(avail.onHandQtyMilli, requiredMilli) : null;
                    const toOrderMilli = avail ? Math.max(requiredMilli - avail.onHandQtyMilli - avail.orderedQtyMilli, 0) : null;
                    return (
                      <tr key={r.id} className="border-b border-line last:border-0">
                        <td className="py-2.5 pl-[18px] pr-3 font-medium text-ink">{r.label}</td>
                        <td className="whitespace-nowrap px-3 py-2.5 capitalize text-ink-2">{r.kind}</td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-right font-mono text-ink-2">{milliToDecimal(r.perPieceMilli)} {r.uom}</td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-right font-mono text-ink-2">{bpToPercent(r.wastageBp)}%</td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-right font-mono font-semibold text-ink">{milliToDecimal(requiredMilli)} {r.uom}</td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-right font-mono">
                          {fromStockMilli !== null && fromStockMilli > 0 ? (
                            <span className="text-ok">{milliToDecimal(fromStockMilli)} {r.uom}</span>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap py-2.5 pl-3 pr-[18px] text-right font-mono font-semibold">
                          {toOrderMilli === null ? (
                            <span className="text-muted">—</span>
                          ) : toOrderMilli > 0 ? (
                            <span className="text-warn">{milliToDecimal(toOrderMilli)} {r.uom}</span>
                          ) : (
                            <span className="text-ok">Covered</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </FieldSectionCard>
        </div>
      ) : null}
      </div>
    </div>
  );
}

/* ── shared bits ────────────────────────────────────────────────── */

function Stat({ label, value, tone }: { label: string; value: number; tone?: "warn" | undefined }) {
  return (
    <div className="px-4 py-3 text-center">
      <div className={`font-mono text-lg font-bold tabular-nums ${tone === "warn" && value > 0 ? "text-warn" : "text-ink"}`}>{value}</div>
      <div className="mt-0.5 text-xs font-semibold uppercase tracking-wide text-muted">{label}</div>
    </div>
  );
}

function NumField({ label, hint, value, onChange, placeholder }: { label: string; hint?: string | undefined; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.04em] text-muted">
        {label}
        {hint ? (
          <span role="img" className="inline-flex items-center cursor-help text-muted/70 hover:text-ink-2" title={hint} aria-label={hint}>
            <Info size={11} aria-hidden />
          </span>
        ) : null}
      </span>
      <input value={value} onChange={(e) => { const v = e.currentTarget.value; onChange(v); }} placeholder={placeholder} inputMode="decimal" className="h-8 rounded-sm border border-line bg-paper px-2 text-right font-mono text-sm text-ink outline-none focus-visible:border-sky-600" />
    </label>
  );
}

/**
 * Shows the field group's arithmetic in plain sight — "what you typed" on the
 * left, the server-computed result (never re-derived client-side) on the
 * right. Same `lastBreakdown` figure the live preview panel already shows.
 */
function FormulaRow({ formula, label, result }: { formula: string; label: string; result: string | null }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-sm bg-raised/60 px-2.5 py-1.5 text-xs">
      <span className="min-w-0 truncate text-muted">
        <span className="font-mono">{formula}</span> <span className="text-muted/80">{label}</span>
      </span>
      <span className="shrink-0 font-mono font-semibold tabular-nums text-ink">{result ?? "—"}</span>
    </div>
  );
}

/**
 * Section-card chrome matching the Purchase Order composer's `.section` /
 * `.section-head` pattern (`po-section-card.tsx`) — copied rather than
 * imported cross-feature (the "Sibling Importer" anti-pattern).
 */
function FieldSectionCard({
  step,
  title,
  description,
  action,
  bodyless = false,
  children,
}: {
  step?: string | undefined;
  title: string;
  description?: string | undefined;
  action?: React.ReactNode | undefined;
  bodyless?: boolean | undefined;
  children: React.ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-md border border-line bg-paper shadow-sm">
      <header className="flex items-start justify-between gap-3 border-b border-line px-[18px] pb-2.5 pt-3.5">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-ink">
            {step ? (
              <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-bg font-mono text-xs font-bold text-muted">
                {step}
              </span>
            ) : null}
            {title}
          </h3>
          {description ? <p className={`mt-0.5 text-xs text-muted ${step ? "ml-7" : ""}`}>{description}</p> : null}
        </div>
        {action ? <div className="flex shrink-0 items-center gap-1.5">{action}</div> : null}
      </header>
      {bodyless ? children : <div className="px-[18px] py-4">{children}</div>}
    </section>
  );
}

function PreviewMetaCell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <span className="mb-0.5 block text-xs font-semibold uppercase tracking-[0.08em] text-muted">{label}</span>
      <span className="block text-xs leading-normal text-ink">{children}</span>
    </div>
  );
}

function PreviewTotalRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1 text-xs text-muted">
      <span>{label}</span>
      <span className="font-mono font-medium tabular-nums text-ink">{value}</span>
    </div>
  );
}

function LockStep({ icon: Icon, label, value }: { icon: typeof Lock; label: string; value: string }) {
  return (
    <div className="flex items-start gap-2 first:pl-0 sm:px-4 sm:first:pl-0 sm:last:pr-0">
      <Icon size={14} className="mt-0.5 shrink-0 text-muted" aria-hidden />
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted">{label}</p>
        <p className="mt-0.5 text-xs leading-snug text-ink-2">{value}</p>
      </div>
    </div>
  );
}

/* ── conversions ────────────────────────────────────────────────── */

function milliToDecimal(milli: number): string {
  return (milli / 1000).toString();
}
function decimalToMilli(input: string): number | null {
  const n = Number(input.trim());
  if (input.trim() === "" || !Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 1000);
}
function percentToBp(input: string): number | null {
  const n = Number(input.trim());
  if (input.trim() === "" || !Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}
function paiseToRupees(paiseStr: string): string {
  return (Number(paiseStr) / 100).toString();
}
function rupeesToPaiseStr(input: string): string | null {
  const n = Number(input.trim());
  if (input.trim() === "" || !Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100).toString();
}
